import React, { useMemo } from "react";

import { useIntl } from "react-intl";

import EntityHistoryAction from "@mapmycustomers/shared/enum/EntityHistoryAction";
import HistoryFieldName from "@mapmycustomers/shared/enum/HistoryFieldName";
import { EntityHistoryRowWithRecord } from "@mapmycustomers/shared/types/entity/EntityHistoryRow";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";

import FieldModel from "@app/util/fieldModel/impl/FieldModel";

interface Props {
  fieldModel: FieldModel;
  historyRow: EntityHistoryRowWithRecord;
}

const FieldColumn: React.FC<Props> = ({ fieldModel, historyRow }) => {
  const intl = useIntl();
  const text = useMemo(() => {
    let field: IField | undefined;
    if (historyRow?.fieldName === HistoryFieldName.ACCOUNT_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.company",
        defaultMessage: "Company",
        description: "History modal - table - company cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.ACCOUNTS_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.companies",
        defaultMessage: "Companies",
        description: "History modal - table - companies cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.CONTACT_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.person",
        defaultMessage: "Person",
        description: "History modal - table - person cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.TERR_IDS) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.territories",
        defaultMessage: "Territories",
        description: "History modal - table - Territories cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.FREQUENCY_ACCEPTED_TYPES) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.frequencyAcceptedTypes",
        defaultMessage: "Frequency: Accepted Types",
        description: "History modal - table - Frequency accepted types cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.PARENT_ACCOUNT_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.parentCompany",
        defaultMessage: "Parent Company",
        description: "History modal - table - parent company cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.PRIMARY_ACCOUNT_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.primaryCompany",
        defaultMessage: "Primary Company",
        description: "History modal - table - primary company cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.PRIMARY_CONTACT_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.primaryPerson",
        defaultMessage: "Primary Person",
        description: "History modal - table - primary person cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.FUNNEL_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.funnel",
        defaultMessage: "Funnel",
        description: "History modal - table - funnel cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.DEAL_STAGE_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.stage",
        defaultMessage: "Stage",
        description: "history modal - table - stage cell",
      });
    } else if (historyRow?.fieldName === HistoryFieldName.DEAL_LOSS_REASON_ID) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.lossReason",
        defaultMessage: "Loss Reason",
        description: "history modal - table - Loss Reason cell",
      });
    } else if (historyRow.actionType === EntityHistoryAction.OWNERSHIP_CHANGED) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.ownership",
        defaultMessage: "Owner",
        description: "History modal - table - ownership cell",
      });
    } else if (historyRow.actionType === EntityHistoryAction.LOCATION_CHANGED) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.location",
        defaultMessage: "Location",
        description: "History modal - table - Location cell",
      });
    } else if (
      [EntityHistoryAction.ADDED_TO_GROUP, EntityHistoryAction.REMOVED_FROM_GROUP].includes(
        historyRow.actionType
      )
    ) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.group",
        defaultMessage: "Group(s)",
        description: "History modal - table - Group(s) cell",
      });
    } else if (
      [EntityHistoryAction.ADDED_TO_ROUTE, EntityHistoryAction.REMOVED_FROM_ROUTE].includes(
        historyRow.actionType
      )
    ) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.route",
        defaultMessage: "Route(s)",
        description: "History modal - table - Route(s) cell",
      });
    } else if (historyRow.actionType === EntityHistoryAction.NOTE_ADDED) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.noteCreated",
        defaultMessage: "Note Created",
        description: "History modal - table - Note Created cell",
      });
    } else if (historyRow.actionType === EntityHistoryAction.NOTE_UPDATED) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.noteUpdated",
        defaultMessage: "Note Updated",
        description: "History modal - table - Note Updated cell",
      });
    } else if (historyRow.actionType === EntityHistoryAction.NOTE_DELETED) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.noteDeleted",
        defaultMessage: "Note Deleted",
        description: "History modal - table - Note Deleted cell",
      });
    } else if (historyRow.actionType === EntityHistoryAction.FILE_ADDED) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.fileAdded",
        defaultMessage: "File Added",
        description: "History modal - table - File Added cell",
      });
    } else if (historyRow.actionType === EntityHistoryAction.FILE_REMOVED) {
      return intl.formatMessage({
        id: "@component.entityHistory.table.field.fileRemoved",
        defaultMessage: "File Removed",
        description: "History modal - table - File Removed cell",
      });
    } else if (
      [
        EntityHistoryAction.CUSTOM_FIELD_VALUE_ADDED,
        EntityHistoryAction.CUSTOM_FIELD_VALUE_DELETED,
        EntityHistoryAction.CUSTOM_FIELD_VALUE_UPDATED,
      ].includes(historyRow.actionType)
    ) {
      field = fieldModel.getByName(historyRow?.customField?.id.toString() ?? "");
    }
    if (historyRow.fieldName && historyRow.actionType === EntityHistoryAction.RECORD_UPDATED) {
      field = fieldModel.getByName(historyRow.fieldName);
    }
    if (field) {
      return field.displayName;
    }
    return historyRow.fieldName ?? "";
  }, [fieldModel, historyRow, intl]);

  return <div>{text}</div>;
};
export default FieldColumn;
